import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CALENDAR_DOC,
  CALENDAR_EVENTS_KEY,
  CHECKINS_DOC,
  CHECKINS_MAP_KEY,
  composeDigest,
  HOUSEHOLD_BIRTHDAYS_KEY,
  HOUSEHOLD_DIGEST_PREFS_KEY,
  HOUSEHOLD_DOC,
  LISTS_DOC,
  birthdaySchema,
  personalCalendarDocName,
  personalListsDocName,
  checkinSchema,
  digestTimeFor,
  listDocName,
  listMetaSchema,
  parseCalendarEvent,
  parseDigestPref,
  type Birthday,
  type CalendarEvent,
  type Checkin,
  type Digest,
  type DigestPref,
  type ListItem,
  type ListMeta,
} from '@awwyeah/shared';
import type * as Y from 'yjs';
import { useYDoc, useYMap } from '../../sync';

/**
 * The digest's Yjs surface — a pure **read** over four docs (ROADMAP §2: "nothing in the
 * digest module ever writes collaborative state"). `composeDigest` itself is a plain
 * function (`@awwyeah/shared`); everything in this file exists to turn live doc snapshots
 * into the plain `DigestSourceDocs` shape it expects.
 *
 * Reading every list's items is the one wrinkle: `list:<id>` doc names aren't known until
 * the `lists` doc says which lists exist, and React hooks can't be called a variable number
 * of times inside one component. `ListItemsBridge` solves it the way `ListsIndexPage`'s
 * `ListSummary` does — one small, invisible component *per list*, each opening its own doc
 * with a fixed hook call count and reporting its items up through a callback.
 */

function useCalendarEvents(): CalendarEvent[] {
  const doc = useYDoc(CALENDAR_DOC);
  const { entries } = useYMap<unknown>(doc, CALENDAR_EVENTS_KEY);
  return useMemo(
    () =>
      entries
        .map(([, value]) => parseCalendarEvent(value))
        .filter((e): e is CalendarEvent => e !== null),
    [entries],
  );
}

/**
 * The signed-in member's own `calendar:user:<id>` events (WS-15). `composeDigest` takes
 * these separately (`personalEventsByUserId`) rather than pre-merged, so the *same* input
 * shape works for the server scheduler, which composes for several members in one pass.
 */
function usePersonalCalendarEvents(userId: string | null): CalendarEvent[] {
  // Hooks can't be conditional: with nobody signed in this re-opens the shared doc, which
  // the DocManager resolves to the same instance, and the result is discarded below.
  const doc = useYDoc(userId ? personalCalendarDocName(userId) : CALENDAR_DOC);
  const { entries } = useYMap<unknown>(doc, CALENDAR_EVENTS_KEY);
  return useMemo(
    () =>
      userId
        ? entries
            .map(([, value]) => parseCalendarEvent(value))
            .filter((e): e is CalendarEvent => e !== null)
        : [],
    [entries, userId],
  );
}

function useBirthdays(): Birthday[] {
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<unknown>(doc, HOUSEHOLD_BIRTHDAYS_KEY);
  return useMemo(
    () =>
      entries
        .map(([, value]) => birthdaySchema.safeParse(value))
        .flatMap((parsed) => (parsed.success ? [parsed.data] : [])),
    [entries],
  );
}

/** `map` is exposed too — the pref control writes straight to it (no REST, ROADMAP §1.3). */
export function useDigestPrefs(): { map: Y.Map<DigestPref>; prefs: Map<string, DigestPref> } {
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { map, entries } = useYMap<DigestPref>(doc, HOUSEHOLD_DIGEST_PREFS_KEY);
  const prefs = useMemo(() => {
    const out = new Map<string, DigestPref>();
    for (const [memberId, value] of entries) {
      const parsed = parseDigestPref(value);
      if (parsed) out.set(memberId, parsed);
    }
    return out;
  }, [entries]);
  return { map, prefs };
}

/**
 * Lists from **both** registries (WS-15) — the shared one and my own. Reading only `lists`
 * would silently drop every private list's tasks out of my morning digest, which is the
 * one place I'd most expect to see them.
 */
function useLists(userId: string | null): ListMeta[] {
  const doc = useYDoc(LISTS_DOC);
  const personalDoc = useYDoc(userId ? personalListsDocName(userId) : LISTS_DOC);
  const { entries } = useYMap<unknown>(doc, 'lists');
  const { entries: personalEntries } = useYMap<unknown>(personalDoc, 'lists');
  return useMemo(() => {
    const byId = new Map<string, ListMeta>();
    const add = (raw: [string, unknown][]): void => {
      for (const [, value] of raw) {
        const parsed = listMetaSchema.safeParse(value);
        if (parsed.success) byId.set(parsed.data.id, parsed.data);
      }
    };
    add(entries);
    if (userId) add(personalEntries);
    return [...byId.values()];
  }, [entries, personalEntries, userId]);
}

function useCheckins(): Checkin[] {
  const doc = useYDoc(CHECKINS_DOC);
  const { entries } = useYMap<unknown>(doc, CHECKINS_MAP_KEY);
  return useMemo(
    () =>
      entries
        .map(([, value]) => checkinSchema.safeParse(value))
        .flatMap((parsed) => (parsed.success ? [parsed.data] : [])),
    [entries],
  );
}

/** Invisible per-list reader; reports its live items up to the parent whenever they change. */
function ListItemsBridge({
  listId,
  onItems,
}: {
  listId: string;
  onItems: (listId: string, items: ListItem[]) => void;
}): null {
  const doc = useYDoc(listDocName(listId));
  const { entries } = useYMap<ListItem>(doc, 'items');
  const items = useMemo(() => entries.map(([, item]) => item), [entries]);

  useEffect(() => {
    onItems(listId, items);
  }, [listId, items, onItems]);

  return null;
}

export interface DigestSources {
  events: CalendarEvent[];
  /** My own `calendar:user:<id>` events, kept separate — see `composeDigest`'s input. */
  personalEvents: CalendarEvent[];
  lists: ListMeta[];
  checkins: Checkin[];
  birthdays: Birthday[];
  digestPrefsMap: Y.Map<DigestPref>;
  digestPrefs: Map<string, DigestPref>;
  /** Mount once per digest page — the list-item readers that keep `itemsByListId` live. */
  listItemBridges: JSX.Element[];
  itemsByListId: Record<string, ListItem[]>;
}

/** Everything the digest needs from Yjs, minus the compose step (kept separate so the view
 * can render the "listening" bridges without re-deriving the digest on every keystroke). */
export function useDigestSources(userId: string | null = null): DigestSources {
  const events = useCalendarEvents();
  const personalEvents = usePersonalCalendarEvents(userId);
  const birthdays = useBirthdays();
  const lists = useLists(userId);
  const checkins = useCheckins();
  const { map: digestPrefsMap, prefs: digestPrefs } = useDigestPrefs();

  const [itemsByListId, setItemsByListId] = useState<Record<string, ListItem[]>>({});
  const onItems = useCallback((listId: string, items: ListItem[]) => {
    setItemsByListId((prev) => (prev[listId] === items ? prev : { ...prev, [listId]: items }));
  }, []);

  const activeListIds = useMemo(
    () => lists.filter((list) => !list.archived).map((list) => list.id),
    [lists],
  );
  const listItemBridges = activeListIds.map((listId) => (
    <ListItemsBridge key={listId} listId={listId} onItems={onItems} />
  ));

  return {
    events,
    personalEvents,
    lists,
    checkins,
    birthdays,
    digestPrefsMap,
    digestPrefs,
    listItemBridges,
    itemsByListId,
  };
}

/** Composes the digest for one member from already-read doc snapshots (pure/memoised). */
export function useComposedDigest(
  sources: DigestSources,
  forUserId: string | null,
  now: Date,
): Digest | null {
  return useMemo(() => {
    if (!forUserId) return null;
    return composeDigest({
      docs: {
        events: sources.events,
        personalEventsByUserId: { [forUserId]: sources.personalEvents },
        lists: sources.lists,
        listItemsByListId: sources.itemsByListId,
        checkins: sources.checkins,
        members: [],
        birthdays: sources.birthdays,
      },
      forUserId,
      now,
    });
  }, [
    sources.events,
    sources.personalEvents,
    sources.lists,
    sources.itemsByListId,
    sources.checkins,
    sources.birthdays,
    forUserId,
    now,
  ]);
}

/** The effective send time for one member (defaults per `digestTimeFor`). */
export function effectiveDigestTime(prefs: Map<string, DigestPref>, memberId: string): string {
  return digestTimeFor(prefs.get(memberId));
}
